import {
  ApplicationVariantType,
  AvailabilityStatus,
  DeferredResponse,
  InputType,
  LicensingProductGroup,
  MySoftwareCriteria,
  MySoftwareCriteriaFilterDataType,
  MySoftwareTab,
  PaginatedAPIResponse,
  SalesOrder,
} from '@order-management/types';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useOutletContext,
} from 'react-router-dom';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { MySoftwarePageOutletContext } from '../my-software-page/my-software-page';
import SoftwareInboxTable from '../software-inbox-table/software-inbox-table';
import {
  fetchMySoftwareSalesOrders as fetchMySoftwareSalesOrdersRequest,
  fetchSoftwareTitles,
} from '../software-service';

const initialCriteria: MySoftwareCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  availabilityStatus: AvailabilityStatus.AVAILABLE,
  type: ApplicationVariantType.NATIVE,
};

const FETCH_MY_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE =
  'My Software could not be fetched. Please try again';

export const loader: LoaderFunction = () => {
  const response = fetchMySoftwareSalesOrdersRequest(initialCriteria);

  return defer({ response });
};

export function MySoftwareDeviceBasedTabContent() {
  const { setActiveTab } = useOutletContext<MySoftwarePageOutletContext>();
  const mySoftwareSalesOrdersInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<SalesOrder>;
  const [
    mySoftwareSalesOrdersDataResponse,
    setMySoftwareSalesOrdersDataResponse,
  ] = useState<PaginatedAPIResponse<SalesOrder>>(
    mySoftwareSalesOrdersInitialResponse
  );
  const [mySoftwareSalesOrders, setMySoftwareSalesOrders] = useState<
    SalesOrder[]
  >(mySoftwareSalesOrdersInitialResponse.data);
  const [mySoftwareFilterCriteria, setMySoftwareFilterCriteria] =
    useState<MySoftwareCriteria>(initialCriteria);
  const [baseMySoftwareCriteria, setBaseMySoftwareCriteria] =
    useState<MySoftwareCriteria>(initialCriteria);
  const {
    executeHttpRequest: executeFetchMySoftwareSalesOrdersRequest,
    isLoading: isLoadingMySoftwareSalesOrders,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreMySoftwareSalesOrdersRequest } =
    useHttpRequest();

  const hasMoreSalesOrders = useMemo(
    () => !!mySoftwareSalesOrdersDataResponse.hasMore,
    [mySoftwareSalesOrdersDataResponse.hasMore]
  );

  const filtersData = useMemo(() => {
    return {
      softwareTitles: {
        title: 'Software',
        appliedCount: mySoftwareFilterCriteria.softwareTitles?.length,
        value: mySoftwareFilterCriteria.softwareTitles,
        input: {
          type: InputType.SELECT,
          valueKey: 'softwareTitles',
          fetchItems: fetchSoftwareTitles,
          resolveOption: (
            item: Pick<LicensingProductGroup, 'id' | 'displayName'>
          ) => ({
            label: item.displayName,
            value: item.displayName,
          }),
        },
      },
    };
  }, [
    mySoftwareFilterCriteria.softwareTitles,
  ]) as FiltersData<MySoftwareCriteria>;

  const customizeViewOptions = useMemo(
    () => [
      {
        title: 'Show software with no available seats',
        applied:
          mySoftwareFilterCriteria.availabilityStatus ===
          AvailabilityStatus.ALL,
        valueKey: 'availabilityStatus',
        checkedValue: AvailabilityStatus.ALL,
        uncheckedValue: AvailabilityStatus.AVAILABLE,
      },
    ],
    [mySoftwareFilterCriteria.availabilityStatus]
  );

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, value]) => value.appliedCount > 0
    );
  }, [filtersData]);

  const resolveBaseMySoftwareCriteria = useCallback(
    (criteria: MySoftwareCriteria) => ({
      itemsPerPage: criteria.itemsPerPage,
      pageNumber: criteria.pageNumber,
      search: criteria.search,
      sortBy: criteria.sortBy,
      sortDirection: criteria.sortDirection,
      availabilityStatus: criteria.availabilityStatus,
      type: criteria.type,
    }),
    []
  );

  const hasBaseMySoftwareCriteriaChanged = useCallback(
    (newCriteria: MySoftwareCriteria) => {
      const keys = Object.keys(initialCriteria) as (keyof MySoftwareCriteria)[];
      return keys.some(
        (key) => newCriteria[key] !== baseMySoftwareCriteria[key]
      );
    },
    [baseMySoftwareCriteria]
  );

  const fetchMySoftwareSalesOrders = useCallback(
    (criteria: MySoftwareCriteria) =>
      executeFetchMySoftwareSalesOrdersRequest({
        asyncFunction: async () => {
          const mySoftwareSalesOrdersResponse =
            await fetchMySoftwareSalesOrdersRequest(criteria);
          setMySoftwareSalesOrders(mySoftwareSalesOrdersResponse.data);
          setMySoftwareSalesOrdersDataResponse(mySoftwareSalesOrdersResponse);
        },
        customErrorMessage: FETCH_MY_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchMySoftwareSalesOrdersRequest]
  );

  const fetchMoreMySoftwareSalesOrders = useCallback(
    () =>
      executeFetchMoreMySoftwareSalesOrdersRequest({
        asyncFunction: async () => {
          const nextPage = mySoftwareFilterCriteria.pageNumber + 1;
          const updatedMySoftwareFilterCriteria = {
            ...mySoftwareFilterCriteria,
            pageNumber: nextPage,
          };
          const mySoftwareSalesOrdersResponse =
            await fetchMySoftwareSalesOrdersRequest(
              updatedMySoftwareFilterCriteria
            );
          setMySoftwareFilterCriteria(updatedMySoftwareFilterCriteria);
          setMySoftwareSalesOrders((prevSalesOrders) =>
            prevSalesOrders.concat(mySoftwareSalesOrdersResponse.data)
          );
          setMySoftwareSalesOrdersDataResponse(mySoftwareSalesOrdersResponse);
        },
        customErrorMessage: FETCH_MY_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE,
      }),
    [executeFetchMoreMySoftwareSalesOrdersRequest, mySoftwareFilterCriteria]
  );

  const onFilterChange = useCallback(
    async (value: MySoftwareCriteriaFilterDataType) => {
      const { itemsPerPage, pageNumber } = initialCriteria;
      const updatedCriteria = {
        ...value,
        itemsPerPage,
        pageNumber,
      } as MySoftwareCriteria;
      setMySoftwareFilterCriteria(updatedCriteria);

      const updatedBaseDeviceGroupsCriteria =
        resolveBaseMySoftwareCriteria(updatedCriteria);
      const criteriaChanged = hasBaseMySoftwareCriteriaChanged(
        updatedBaseDeviceGroupsCriteria
      );

      if (criteriaChanged) {
        setBaseMySoftwareCriteria(updatedBaseDeviceGroupsCriteria);
        await fetchMySoftwareSalesOrders(updatedBaseDeviceGroupsCriteria);
      }
    },
    [
      fetchMySoftwareSalesOrders,
      hasBaseMySoftwareCriteriaChanged,
      resolveBaseMySoftwareCriteria,
    ]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof MySoftwareCriteria)[]) => {
      const newData = { ...mySoftwareFilterCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      onFilterChange(newData);
    },
    [mySoftwareFilterCriteria, onFilterChange]
  );

  useEffect(() => {
    setActiveTab(MySoftwareTab.DEVICE_BASED_SOFTWARE);
  }, [setActiveTab]);

  useEffect(() => {
    if (mySoftwareSalesOrdersInitialResponse) {
      setMySoftwareSalesOrders(mySoftwareSalesOrdersInitialResponse.data);
      setMySoftwareSalesOrdersDataResponse(
        mySoftwareSalesOrdersInitialResponse
      );
    }
  }, [mySoftwareSalesOrdersInitialResponse]);

  return (
    <>
      <Element display="flex">
        <FilterBar
          initialData={initialCriteria}
          data={mySoftwareFilterCriteria}
          filtersData={filtersData}
          onFilterSubmit={onFilterChange}
          onRemoveFilter={onRemoveFilters}
          customizeViewOptions={customizeViewOptions}
        />
      </Element>
      <SoftwareInboxTable
        salesOrders={mySoftwareSalesOrders}
        fetchMoreSalesOrders={fetchMoreMySoftwareSalesOrders}
        hasMoreSalesOrders={hasMoreSalesOrders}
        loadingInitialData={isLoadingMySoftwareSalesOrders}
        mySoftwareCriteriaFilter={mySoftwareFilterCriteria}
        appliedFilters={appliedFilters}
        softwareVariantType={ApplicationVariantType.NATIVE}
        enableSearch={false}
      />
    </>
  );
}

export function MySoftwareDeviceBasedTab() {
  const { response } = useLoaderData() as DeferredResponse<
    PaginatedAPIResponse<SalesOrder>
  >;

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <MySoftwareDeviceBasedTabContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default MySoftwareDeviceBasedTab;
